import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ChatMessage = ({ message, onProductClick, onQuickAction }) => {
  const isUser = message.sender === 'user';
  const isExpert = message.sender === 'expert';

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-6`}>
      <div className={`flex ${isUser ? 'flex-row-reverse' : 'flex-row'} items-start space-x-3 max-w-4xl`}>
        {/* Avatar */}
        <div className="flex-shrink-0">
          {isUser ? (
            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
              <Icon name="User" size={20} color="white" />
            </div>
          ) : isExpert ? (
            <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center">
              <Icon name="Crown" size={20} color="white" />
            </div>
          ) : (
            <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
              <Icon name="Sparkles" size={20} color="white" />
            </div>
          )}
        </div>

        {/* Message Content */}
        <div className={`flex-1 ${isUser ? 'mr-3' : 'ml-3'}`}>
          {/* Sender Name & Time */}
          <div className={`flex items-center space-x-2 mb-1 ${isUser ? 'justify-end' : 'justify-start'}`}>
            <span className="text-sm font-medium text-accent-700">
              {isUser ? 'You' : isExpert ? message.expertName : 'Giftsy AI'}
            </span>
            {isExpert && (
              <div className="flex items-center space-x-1">
                <Icon name="Verified" size={14} className="text-secondary" />
                <span className="text-xs text-secondary-600">{message.expertise}</span>
              </div>
            )}
            <span className="text-xs text-text-muted">{formatTime(message.timestamp)}</span>
          </div>

          {/* Message Bubble */}
          <div className={`rounded-2xl px-4 py-3 ${
            isUser 
              ? 'bg-primary text-white chat-bubble' :'bg-white border border-accent-200 shadow-sm'
          }`}>
            {/* Text Content */}
            {message.content && (
              <p className={`text-sm leading-relaxed ${isUser ? 'text-white' : 'text-accent-700'}`}>
                {message.content}
              </p>
            )}

            {/* Image Attachment */}
            {message.image && (
              <div className="mt-3">
                <div className="rounded-lg overflow-hidden">
                  <Image 
                    src={message.image} 
                    alt="Shared inspiration" 
                    className="w-full h-48 object-cover"
                  />
                </div>
              </div>
            )}

            {/* Product Recommendations */}
            {message.products && message.products.length > 0 && (
              <div className="mt-4 space-y-3">
                <p className="text-sm font-medium text-accent-700 mb-3">Here are some perfect matches:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {message.products.map((product) => (
                    <div 
                      key={product.id}
                      className="bg-surface rounded-xl p-3 border border-accent-100 hover:border-primary-200 transition-colors cursor-pointer"
                      onClick={() => onProductClick(product)}
                    >
                      <div className="flex space-x-3">
                        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                          <Image 
                            src={product.image} 
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm text-accent-800 truncate">{product.name}</h4>
                          <p className="text-xs text-text-secondary mt-1 line-clamp-2">{product.description}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="font-semibold text-primary">${product.price}</span>
                            <div className="flex items-center space-x-1">
                              <Icon name="Star" size={12} className="text-warning-400 fill-current" />
                              <span className="text-xs text-text-secondary">{product.rating}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            {message.quickActions && message.quickActions.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {message.quickActions.map((action, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => onQuickAction(action)}
                    className="text-xs"
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;